<template>
    <pc-modal
        ref="modal"
        v-bind:large="large"
        v-bind:extra-large="extraLarge"
        v-bind:small="small"
        v-bind:side-panel="sidePanel"
        v-bind:left="left"
        v-bind:fit-content="fitContent"
        v-bind:show-header="showHeader"
        v-bind:show-footer="showFooter"
        v-bind:hide-body-padding="hideBodyPadding"
        v-bind:scrollable="scrollable"
        v-bind:data-test-role="testRole"
        v-bind:dismiss-on-outside-click="dismissOnOutsideClick"
        v-on:shown="onModalShown"
        v-on:hidden="onModalHidden"
        v-on:keyup.enter="onEnter">
        <template
            v-if="showHeader"
            v-slot:header>
            <span class="h4 m-0">{{ title }}</span>
        </template>
        <template v-if="hasCustomBody">
            <component
                v-bind="customComponentOptions"
                v-bind:is="customComponent"
                ref="customComponent"
                v-on="customComponentListeners" />
        </template>
        <template v-else>
            {{ message }}
        </template>
        <template
            v-if="showFooter"
            v-slot:footer>
            <pc-button
                v-for="(button, index) in buttons"
                v-bind:id="button.id"
                v-bind:key="index"
                v-bind:primary="button.primary"
                v-bind:warning="button.warning"
                v-bind:success="button.success"
                v-bind:disabled="button.disabled"
                v-bind:data-test-role="button.testRole"
                class="btn"
                v-on:click="onButtonClick(button.result)">
                {{ button.text }}
            </pc-button>
        </template>
    </pc-modal>
</template>

<script>
    import { markRaw } from "vue";
    /**
     * Consts that can be used to name the hooks implemented by components
     * that are presented in dialogs and referenced by the components that
     * trigger the dialogs. These are intended as a tool for custom dialog
     * components, but it is not required to use them. See dialog samples
     * in responsiveTestBench for more detail.
     */
    export const DIALOG_HOOK = {
        isValidToClose: "isValidToClose",
        getResult: "getResult"
    };

    /**
     * Consts that can be used to name the possible result actions by components
     * that are presented in dialogs and referenced by the components that trigger
     * the dialogs. These basically correspond to the buttons that the component
     * calling "showDialog" puts on the dialog. These are intended as a tool for
     * custom dialog components, but it is not required to use them. A specific
     * custom dialog may need to implement different or additional result options,
     * but these should help in many general cases. See dialog samples in
     * responsiveTestBench for more detail.
     */
    export const DIALOG_RESULT = {
        ok: "OK",
        cancel: "CANCEL"
    };

    export default {
        data() {
            return {
                title: null,
                buttons: null,
                dismissOnOutsideClick: false,
                customComponent: null,
                customComponentOptions: null,
                customComponentResultHook: null,
                customComponentIsValidToCloseHookAsync: null,
                message: null,
                promise: null,
                large: false,
                extraLarge: false,
                small: true,
                sidePanel: false,
                left: false,
                fitContent: false,
                hideBodyPadding: false,
                scrollable: true,
                testRole: null,
                resolutionObject: null
            };
        },
        computed: {
            showFooter() {
                return Boolean(this.buttons && this.buttons.length > 0);
            },
            showHeader() {
                return Boolean(this.title);
            },
            hasCustomBody() {
                return Boolean(this.customComponent);
            }
        },
        methods: {
            // If this throws an error such as:
            //   TypeError: proxy set handler returned false for property '<prop>'
            // Make sure you're sending a non-reactive object to the dialog
            reset() {
                this.title = null;
                this.buttons = null;
                this.dismissOnOutsideClick = false;
                this.customComponent = null;
                this.customComponentOptions = null;
                this.customComponentResultHook = null;
                this.customComponentIsValidToCloseHookAsync = null;
                this.message = null;
                this.large = false;
                this.extraLarge = false;
                this.small = true;
                this.sidePanel = false;
                this.left = false;
                this.fitContent = false;
                this.hideBodyPadding = false;
                this.scrollable = true;
                this.testRole = null;
                if (this.promiseWrapper) {
                    this.promiseWrapper.reject();
                    this.promiseWrapper = null;
                }
            },
            show(options) {
                this.reset();
                const customComponent = options.customComponent;
                if (customComponent) {
                    markRaw(customComponent);
                }
                this.$nextTick(() => {
                    this.title = options.title;
                    this.buttons = options.buttons;
                    this.dismissOnOutsideClick = Boolean(options.dismissOnOutsideClick);
                    this.customComponent = customComponent;
                    this.customComponentOptions = options.customComponentOptions;
                    this.customComponentListeners = options.customComponentListeners || {};
                    this.customComponentResultHook = options.customComponentResultHook;
                    this.customComponentIsValidToCloseHookAsync = options.customComponentIsValidToCloseHookAsync;
                    this.message = options.message;
                    this.large = Boolean(options.large);
                    this.extraLarge = Boolean(options.extraLarge);
                    this.small = Boolean(options.small);
                    this.sidePanel = Boolean(options.sidePanel);
                    this.left = Boolean(options.left);
                    this.fitContent = Boolean(options.fitContent);
                    this.hideBodyPadding = Boolean(options.hideBodyPadding);
                    this.testRole = options.testRole;

                    if (options.scrollable != null) {
                        this.scrollable = Boolean(options.scrollable);
                    }
                    this.$refs.modal.show();
                });

                // we want to extract the resolve and reject methods provided by the native promise.
                // we do that via a closure
                let resolve = null;
                let reject = null;
                const promise = new Promise((_resolve, _reject) => {
                    resolve = _resolve; // capture internal _resolve into resolve
                    reject = _reject; // same, but for _reject/reject
                });

                // store all promise objects in this.promiseWrapper for easier access.
                this.promiseWrapper = {
                    promise,
                    resolve,
                    reject
                };
                return this.promiseWrapper.promise;
            },
            hide({ resolve, resolveValue, rejectReason }) {
                this.resolutionObject = {
                    resolve: resolve,
                    resolveValue: resolveValue,
                    rejectReason: rejectReason
                };
                this.$refs.modal.hide();
            },
            async onButtonClick(result) {
                if (
                    this.$refs.customComponent &&
                    typeof this.$refs.customComponent[this.customComponentIsValidToCloseHookAsync] === "function"
                ) {
                    // Check if the component is in a valid state to close the dialog
                    if (!(await this.$refs.customComponent[this.customComponentIsValidToCloseHookAsync](result))) {
                        return;
                    }
                }
                if (
                    this.$refs.customComponent &&
                    typeof this.$refs.customComponent[this.customComponentResultHook] === "function"
                ) {
                    const data = this.$refs.customComponent[this.customComponentResultHook](result);
                    result = { action: result, data: data };
                }
                this.hide({ resolve: true, resolveValue: result });
            },
            onModalShown() {
                if (this.$refs.customComponent && typeof this.$refs.customComponent.onModalShown === "function") {
                    this.$refs.customComponent.onModalShown();
                }
            },
            onModalHidden() {
                if (this.promiseWrapper && this.resolutionObject) {
                    if (this.resolutionObject.resolve) {
                        this.promiseWrapper.resolve(this.resolutionObject.resolveValue);
                    } else {
                        this.promiseWrapper.reject(this.resolutionObject.rejectReason);
                    }
                }
                this.promiseWrapper = null;
                this.resolutionObject = null;
            },
            onEnter() {
                if (!this.buttons || this.buttons.length <= 0) {
                    return;
                }
                const button = this.buttons.find((b) => b.applyOnEnterKeypress);
                if (button && button.result) {
                    this.onButtonClick(button.result);
                }
            }
        }
    };
</script>
